import { FC, useEffect, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useMap } from 'react-leaflet'
import { GeometryItem } from './GeometryItem.tsx'
import { Poi } from '@/js/types'
import { ColorPicker } from '@/js/editor/components/general/inputs/ColorPicker.tsx'
import { AddGeometry } from '@/js/editor/components/general/add/AddGeometry.tsx'
import { useDrawUndo } from '@/js/editor/hooks/useDrawUndo.ts'
import { ControlHints } from '@/js/editor/components/poi/ControlHints.tsx'
import { mapDataAtom } from '@/js/editor/state/mapAtoms.ts'
import { addPoiGeometryAtom, updatePoiDraftAtom } from '@/js/editor/state/poiAtoms.ts'
import { Layer, LeafletEvent } from 'leaflet'
import { GeometryObject } from 'geojson'

type GeometriesProps = {
    poi: Poi
}

export const GeometryListing: FC<GeometriesProps> = ({ poi }) => {
    const [drawMode, setDrawMode] = useState<'Marker' | 'Polygon' | null>(null)
    const { colors: defaultColors } = useAtomValue(mapDataAtom)
    const updateDraft = useSetAtom(updatePoiDraftAtom)
    const addGeometry = useSetAtom(addPoiGeometryAtom)
    const map = useMap()

    useDrawUndo(() => setDrawMode(null))

    useEffect(() => {
        if (drawMode === 'Marker') {
            map.pm.enableDraw('CircleMarker')
        } else if (drawMode === 'Polygon') {
            map.pm.enableDraw('Polygon')
        } else {
            map.pm.disableDraw()
        }

        const handleDrawEnd = (event: LeafletEvent & { layer: Layer }) => {
            const feature = event.layer.toGeoJSON()

            addGeometry(feature.geometry as GeometryObject)
            setDrawMode(null)
        }

        map.on('pm:create', handleDrawEnd)

        return () => {
            map.off('pm:create', handleDrawEnd)
        }
    }, [drawMode, map])

    const updateColor = (color: string) => {
        updateDraft({ color })
    }

    const updateHighlightColor = (highlight_color: string | null) => {
        updateDraft({ highlight_color })
    }

    return (
        <div className={'flex-col pb-5 overflow-hidden'}>
            <div className={'flex flex-row justify-between items-center py-3 relative'}>
                <p className={'text-lg font-bold'}>Markierungen</p>

                <AddGeometry setDrawMode={setDrawMode} />
                {drawMode && <ControlHints mode={drawMode} />}
            </div>

            {poi.geometries?.geometries?.length ? (
                poi.geometries.geometries.map((geometry, index) => (
                    <GeometryItem geometry={geometry} geometryIndex={index} key={index} />
                ))
            ) : (
                <div className={'border-b'} />
            )}

            <div className={'flex flex-row mt-4 flex-wrap gap-3'}>
                <ColorPicker
                    label={'Farbe'}
                    value={poi.color ?? defaultColors.standard}
                    onChange={updateColor}
                    defaultColor={defaultColors.standard}
                />

                <ColorPicker
                    label={'Akzentfarbe'}
                    value={poi.highlight_color ?? defaultColors.highlight}
                    onChange={updateHighlightColor}
                    defaultColor={defaultColors.highlight}
                />
            </div>
        </div>
    )
}
